import FromJson from "../../utils/FromJson";
import _ from "lodash";

export class BusinessAccountLabel {
    text: string;
    constructor(json: any) {
        const p = FromJson(json);
        this.text = p.string("text");
    }
}

export class BusinessAccountAction {
    text: string;
    url: string;
    constructor(text: string, url: string) {
        this.text = text;
        this.url = url;
    }

    static fromJson(json: any) {
        const p = FromJson(json);
        const text = p.string("text");
        const url = p.string("url");
        return new BusinessAccountAction(text, url);
    }
}

export class MenuEntry {
    text: string;
    url: string;
    constructor(text: string, url: string) {
        this.text = text;
        this.url = url;
    }
    static fromJson(json: any): MenuEntry {
        const p = FromJson(json);
        const text = p.string("text");
        const url = p.string("url");
        return new MenuEntry(text, url);
    }
    static fromAction(a: BusinessAccountAction): MenuEntry {
        return new MenuEntry(a.text, a.url);
    }
}

/**
 */
export class BusinessAccount {
    id: string;
    corporateName: string;
    address: string;
    abbreviation: string;
    color: string;
    actions: { [k: string]: BusinessAccountAction };
    menuEntries: Array<MenuEntry>;
    validated: boolean;

    constructor(
        id: string,
        corporateName: string,
        address: string,
        abbreviation: string,
        color: string,
        actions: { [k: string]: BusinessAccountAction },
        menuEntries: Array<MenuEntry>,
        validated: boolean
    ) {
        this.id = id;
        this.corporateName = corporateName;
        this.address = address;
        this.abbreviation = abbreviation;
        this.color = color;
        this.actions = actions;
        this.menuEntries = menuEntries;
        this.validated = validated;
    }

    static fromJson(json: any): BusinessAccount {
        const p = FromJson(json);
        const id = p.string("id");
        const corporateName = p.string("corporateName");
        const address = p.string("address");
        const abbreviation = p.string("abbreviation");
        const color = p.string("color");
        const rawActions = p.object("actions");
        const validated = p.booleanOptional("validated") || false;
        const actions = {} as { [k: string]: BusinessAccountAction };
        _.forEach(_.keys(rawActions), (key) => {
            actions[key] = BusinessAccountAction.fromJson(rawActions[key]);
        });
        const menuEntries = _.map(p.array("menuEntries"), (raw) => {
            return MenuEntry.fromJson(raw);
        });
        return new BusinessAccount(id, corporateName, address, abbreviation, color, actions, menuEntries, validated);
    }
}

export class Person {
    id: string;
    name: string;

    constructor(id: string, name: string) {
        this.id = id;
        this.name = name;
    }
    static fromJson(json: any): Person {
        const p = FromJson(json);
        const id = p.string("id");
        const name = p.string("name");
        return new Person(id, name);
    }
}

export class BusinessAccountResponse {
    accountData: Array<BusinessAccount>;
    activeAccountId: string;
    person: Person;
    labels: { [k: string]: BusinessAccountLabel };

    constructor(
        accountData: Array<BusinessAccount>,
        activeAccountId: string,
        person: Person,
        labels: { [k: string]: BusinessAccountLabel }
    ) {
        this.accountData = accountData;
        this.activeAccountId = activeAccountId;
        this.person = person;
        this.labels = labels;
    }

    static fromJson(json: any): BusinessAccountResponse {
        const p = FromJson(json);
        const person = Person.fromJson(p.object("person"));
        const activeAccountId = p.string("activeAccountId");
        let accountData;
        try {
            const array = p.arrayOptional("accountData");
            accountData = _.compact(
                _.map(array, (json) => {
                    try {
                        return BusinessAccount.fromJson(json);
                    } catch (e) {
                        // crazy cast because flow does not understand that _filter filters all the null values:
                        return null as any;
                    }
                })
            );
        } catch (e) {
            // if we cannot parse it at all,
            // we still shouldn't let the exception escape
            accountData = [];
        }
        const rawLabels = p.object("labels");
        const labels = {} as { [k: string]: BusinessAccountLabel };
        _.forEach(_.keys(rawLabels), (key) => {
            labels[key] = new BusinessAccountLabel(rawLabels[key]);
        });
        return new BusinessAccountResponse(accountData, activeAccountId, person, labels);
    }

    getActiveAccount(): BusinessAccount | undefined | null {
        return (
            _.find(this.accountData, (a) => {
                return a.id === this.activeAccountId;
            }) || null
        );
    }
}
